import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal, Button } from '../../components';
import { changeOwnListingVisibility } from './ListingPage.duck';
import css from './SectionListingVisibility.module.css';

/** Owner-only visibility control, with explicit confirmation and persistent feedback. */
export const ListingVisibilityControl = ({
  listing,
  currentUser,
  onChange,
  onManageDisableScrolling,
}) => {
  const intl = useIntl();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [completed, setCompleted] = useState(null);
  const owner = currentUser?.id?.uuid && currentUser.id.uuid === listing?.author?.id?.uuid;
  const state = listing?.attributes?.state;
  if (!owner || !['published', 'closed'].includes(state)) return null;
  const closing = state === 'published';
  const action = closing ? 'close' : 'reopen';
  const message = key => <FormattedMessage id={`ListingVisibility.${key}`} />;
  const dismiss = () => {
    if (!busy) setConfirming(false);
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const nextState = closing ? 'closed' : 'published';
      await onChange(listing.id, nextState);
      setCompleted(nextState);
      setConfirming(false);
    } catch (_) {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={css.root}>
      <h2 className={css.heading}>{message('title')}</h2>
      <p>{message(closing ? 'published' : 'closed')}</p>
      {completed ? (
        <p className={css.feedback} role="status">
          {message(`${completed}Success`)}
        </p>
      ) : null}
      <button
        id="listing-visibility-action"
        className={css.action}
        type="button"
        onClick={() => {
          setError(false);
          setConfirming(true);
        }}
      >
        {message(action)}
      </button>
      <Modal
        id="ListingVisibility"
        isOpen={confirming}
        onClose={dismiss}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        containerClassName={css.modal}
        focusElementId="listing-visibility-action"
        closeButtonMessage={intl.formatMessage({ id: 'ListingVisibility.cancel' })}
      >
        <h2 className={css.heading}>{message(`${action}Title`)}</h2>
        <p>{message(`${action}Explanation`)}</p>
        {error ? <p role="alert">{message('error')}</p> : null}
        <div className={css.buttons}>
          <button className={css.action} type="button" disabled={busy} onClick={dismiss}>
            {message('cancel')}
          </button>
          <Button type="button" inProgress={busy} disabled={busy} onClick={submit}>
            {message(action)}
          </Button>
        </div>
      </Modal>
    </section>
  );
};

const SectionListingVisibility = props => {
  const dispatch = useDispatch();
  return (
    <ListingVisibilityControl
      {...props}
      onChange={(id, state) => dispatch(changeOwnListingVisibility(id, state))}
    />
  );
};
export default SectionListingVisibility;
